import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { Header } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import useKYCStatusRedirect from '../hooks/useKYCStatusRedirect';
import { FiBookOpen, FiArrowRight, FiTag, FiSearch } from 'react-icons/fi';
import SkeletonTable from '../components/common/SkeletonTable';

const levelColors = {
  primary:   { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200'   },
  secondary: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  nursery:   { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200'  },
};

const ClassAddons = () => {
  useKYCStatusRedirect("/class-addons");
  const { school } = useStateContext();
  const [classesWithAddons, setClassesWithAddons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const schoolCode = school?.code;

  useEffect(() => {
    if (schoolCode) fetchClasses();
  }, [schoolCode]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const { data } = await userRequest.get(`classes?code=${schoolCode}`);
      const classes = data.classes || [];
      const withCounts = classes.map((cls) => ({
        ...cls,
        addonCount: (cls.add_on || []).filter(Boolean).length,
      }));
      setClassesWithAddons(withCounts);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      <div className="mb-6">
        <Header category="Payments" title="Extra Fees" />
        <p className="text-sm text-gray-400 -mt-2">
          Select a class to view and manage its extra fees.
        </p>
      </div>

      {/* Toolbar */}
      {!loading && classesWithAddons.length > 0 && (
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-gray-500">
            {filterText
              ? `${classesWithAddons.filter(c => c.class_name?.toLowerCase().includes(filterText.toLowerCase())).length} of ${classesWithAddons.length} classes`
              : `${classesWithAddons.length} class${classesWithAddons.length !== 1 ? 'es' : ''}`}
          </p>
          <div className="relative mb-8">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Search classes..."
              className="pl-9 pr-4 py-2 text-sm rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 w-48 md:w-64"
            />
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={4} columns={3} />
      ) : classesWithAddons.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No classes found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {classesWithAddons.filter(cls =>
            cls.class_name?.toLowerCase().includes(filterText.toLowerCase())
          ).map((cls) => {
            const level = cls.level_of_education?.toLowerCase();
            const colors = levelColors[level] || levelColors.primary;

            return (
              <Link key={cls._id} to={`/addon-payments/${cls._id}/${encodeURIComponent(cls.class_name)}`}>
                <div className="rounded-2xl border border-gray-200 bg-white p-5 flex flex-col gap-4 hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer group">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl ${colors.bg}`}>
                        <FiBookOpen className={`text-lg ${colors.text}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 capitalize">{cls.class_name}</p>
                        <span className={`inline-block mt-0.5 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${colors.bg} ${colors.text} border ${colors.border}`}>
                          {cls.level_of_education}
                        </span>
                      </div>
                    </div>

                    {/* Addon count badge */}
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
                      ${cls.addonCount > 0 ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}
                    >
                      <FiTag className="w-3 h-3" />
                      {cls.addonCount} {cls.addonCount === 1 ? 'fee' : 'fees'}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      {cls.addonCount > 0 ? 'View extra fees' : 'No extra fees configured'}
                    </span>
                    <FiArrowRight className="text-gray-300 group-hover:text-indigo-500 transition-colors" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassAddons;
